import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGXLogger } from 'ngx-logger';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  concat,
  finalize,
  first,
  interval,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import {
  AdvancedLink,
  AdvancedLinks,
  AppendType,
  ArchiveEmailDocument,
  ArchiveMergeDocument,
  ArchiveProvider,
  BatchPrint,
  DocumentAnnotations,
  DocumentProvider,
  DocumentUpdateProvider,
  EmailArchiveRequest,
  Fields,
  InboxMergeDocument,
  InboxProvider,
  MergeResult,
  MergeTarget,
  MergeTargetType,
  PrintProvider,
  SearchResults,
  UserFriendlyError,
} from 'models';
import {
  ARCHIVE_PROVIDER,
  DOCUMENT_PROVIDER,
  DOCUMENT_UPDATE_PROVIDER,
  INBOX_PROVIDER,
  PRINT_PROVIDER,
} from 'src/app/common/tokens';
import {
  ActionsMenu,
  DocumentUpdateSession,
  SearchResultDocumentOpenRequest,
} from 'src/app/models';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DocumentTransferService } from 'src/app/services/document-transfer.service';
import { GseService } from 'src/app/services/gse.service';
import { LayoutService } from 'src/app/services/layout.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ProgressDialogService } from 'src/app/services/progress-dialog.service';
import { WindowService } from 'src/app/services/window.service';
import { AdvancedLinkQuery } from 'src/app/state/advanced-links/advanced-links.query';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { FieldsQuery } from 'src/app/state/fields/fields.query';
import { SearchesQuery } from 'src/app/state/searches/searches.query';

import { assertExists } from 'common';
import { derivedAsync } from 'ngxtension/derived-async';
import { applyAdvancedLinkUrlReplacements } from 'src/app/common/utility';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { CommandService } from 'src/app/modules/command-palette';
import { SupportedFeaturesService } from 'src/app/services/supported-features.service';
import { ApplicationService } from 'src/app/state/application/application.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import {
  EnhancedExportDialogComponent,
  EnhancedExportDialogData,
} from '../enhanced-export-dialog/enhanced-export-dialog.component';
import {
  InsertFromInboxDialogComponent,
  InsertFromInboxDialogResult,
} from '../insert-from-inbox-dialog/insert-from-inbox-dialog.component';
import {
  InsertFromSearchDialogComponent,
  InsertFromSearchDialogResult,
} from '../insert-from-search-dialog/insert-from-search-dialog.component';
import { CopyableMenuBase } from '../menu/menu-base.component';
import { MergeDocumentsResultDialogComponent } from '../merge-documents-result-dialog/merge-documents-result-dialog.component';
import { UpdateMultipleSearchResultsDialogComponent } from '../update-multiple-search-results-dialog/update-multiple-search-results-dialog.component';

/** Command name for the toggle modify document command. */
const MODIFY_DOCUMENT_COMMAND_NAME = 'Toggle Modify';
/*** Group name for the toggle modify document command. */
const MODIFY_DOCUMENT_COMMAND_GROUP = 'Document';

/** Archive actions menu. */
@UntilDestroy()
@Component({
  selector: 'app-archive-actions-menu',
  templateUrl: './archive-actions-menu.component.html',
  styleUrls: ['./archive-actions-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArchiveActionsMenuComponent
  extends CopyableMenuBase
  implements OnChanges, OnDestroy, OnInit, ActionsMenu
{
  /** Reference for the file input element. */
  @ViewChild('fileInput')
  fileInput: ElementRef<HTMLInputElement>;
  /** Event raised when selected rows should be downloaded as a CSV. */
  @Output()
  downloadSelectedRowsAsCsv = new EventEmitter<boolean>();
  /** If elements related to specifically multiple document contexts should be hidden. */
  @Input()
  hideMultiple: boolean;
  /** If the open option should be hidden. */
  @Input()
  hideOpen: boolean;
  /** Event raised when selected rows should be opened. */
  @Output()
  openSelectedDocuments = new EventEmitter<SearchResultDocumentOpenRequest>();
  /** If the modify document option should be shown. */
  @Input()
  showModify: boolean;
  /** Event triggered when the search should be refreshed. */
  @Output()
  refreshSearch: EventEmitter<any> = new EventEmitter();
  /**
   * Selected search results.
   *
   * This value is normally provided by the openMenu function; however, it can
   * be set by this Input attribute binding as well.
   */
  @Input()
  searchResults: SearchResults = [];
  /** Emits selected search results on change. */
  @Output()
  searchResultsChange = new EventEmitter<SearchResults>();
  /** If modify document mode is enabled. */
  @Input()
  modifyDocumentEnabled = false;
  /** Emits if the modify document button is clicked. */
  @Output()
  modifyDocumentEnabledChange = new EventEmitter<boolean>();

  /** Observable collection of advanced links. */
  advancedLinks$: Observable<AdvancedLinks> = this.advancedLinkQuery
    .selectAll()
    .pipe(
      map((advancedLinks) =>
        advancedLinks.filter((advancedLink) =>
          this.shouldShowAdvancedLink(advancedLink)
        )
      )
    );
  /** Is enhanced export all supported using the connected API. */
  enhancedExportAllSupported = this.supportedFeatures.versionSupports(
    this.supportedFeatures.enhancedExportAll
  );
  /** Observable connection state of GSE. */
  gseIsConnected$ = this.gse.isConnected$;
  /** Observable connection state for launch document. */
  gseIsLaunchAvailable$ = this.gse.isLaunchAvailable$;
  /** Observable connectionstate for Quickbooks. */
  gseIsQuickbooksAvailable$ = this.gse.isQuickbooksAvailable$;
  /** Observable determining if there are advanced links to be shown. */
  hasAdvancedLinks$ = this.advancedLinks$.pipe(
    map((advancedLinks) => advancedLinks.length > 0)
  );
  /** Observable boolean determining if there are favorite searches. */
  hasFavoriteSearches$ = this.searchesQuery.favoritedSearches$.pipe(
    map((favoriteSearches) => favoriteSearches.length > 0)
  );
  /** If the open in the external full viewer option should be hidden. */
  hideOpenFullViewer = derivedAsync(
    () =>
      combineLatest([
        this.appQuery.viewerUseInternal$,
        this.appConfigQuery.hideFullViewer$,
      ]).pipe(
        map(
          ([viewerUseInternal, hideFullViewer]) =>
            !viewerUseInternal || hideFullViewer || this.auth.isGuest
        )
      ),
    { initialValue: false }
  );
  /** Is modify document supported using the connected API. */
  modifyDocumentsSupported = this.supportedFeatures.versionSupports(
    this.supportedFeatures.modifyDocuments
  );

  private useCompact: boolean;

  constructor(
    private logger: NGXLogger,
    private databasesQuery: DatabasesQuery,
    private archivesQuery: ArchivesQuery,
    private searchesQuery: SearchesQuery,
    private appQuery: ApplicationQuery,
    private advancedLinkQuery: AdvancedLinkQuery,
    private fieldsQuery: FieldsQuery,
    private appService: ApplicationService,
    private appConfigQuery: AppConfigQuery,
    private auth: AuthenticationService,
    @Inject(DOCUMENT_PROVIDER)
    private documentProvider: DocumentProvider,
    @Inject(DOCUMENT_UPDATE_PROVIDER)
    private documentUpdateProvider: DocumentUpdateProvider,
    @Inject(PRINT_PROVIDER)
    private printProvider: PrintProvider,
    @Inject(ARCHIVE_PROVIDER)
    private archiveProvider: ArchiveProvider,
    @Inject(INBOX_PROVIDER)
    private inboxProvider: InboxProvider,
    private windowService: WindowService,
    private translate: TranslocoService,
    private progressDialogService: ProgressDialogService,
    private notifications: NotificationService,
    private documentTransferService: DocumentTransferService,
    private layout: LayoutService,
    private gse: GseService,
    private supportedFeatures: SupportedFeaturesService,
    private commandService: CommandService,
    private dialog: MatDialog
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const searchResultsChanges = changes.searchResults;
    const showModifyChanges = changes.showModify;
    if (searchResultsChanges || showModifyChanges) {
      // Remove any existing document commands and then check if they should be added anytime inputs change.
      const modifyCommandExists = this.commandService.commands.some(
        (c) =>
          c.name === MODIFY_DOCUMENT_COMMAND_NAME &&
          c.group === MODIFY_DOCUMENT_COMMAND_GROUP
      );
      if (modifyCommandExists) {
        this.logger.debug(
          'Modify document command already exists. Clearing it before it is potentially recreated.'
        );
        this.commandService.unregister(
          MODIFY_DOCUMENT_COMMAND_NAME,
          MODIFY_DOCUMENT_COMMAND_GROUP
        );
      }
      // Modify enabled is an input that tells this component that the consumer wants the modify menu option.
      // Modify disabled checks if the user can actually modify the current document.
      if (
        this.showModify &&
        this.modifyDocumentsSupported &&
        !this.modifyDocumentDisabled
      ) {
        this.commandService.register(
          MODIFY_DOCUMENT_COMMAND_NAME,
          'Open or close the modify document thumbnailer',
          () => {
            this.logger.debug('Modify document selected from command palette.');
            this.onClickModify();
          },
          MODIFY_DOCUMENT_COMMAND_GROUP
        );
      }
    }
  }

  ngOnDestroy(): void {
    // Ensure modify document is removed from the command palette.
    this.commandService.unregister(
      MODIFY_DOCUMENT_COMMAND_NAME,
      MODIFY_DOCUMENT_COMMAND_GROUP
    );
  }

  ngOnInit(): void {
    this.layout.useCompactLayout$
      .pipe(untilDestroyed(this))
      .subscribe((useCompact) => (this.useCompact = useCompact));
  }

  /**
   * Determines if the append button should be disabled.
   *
   * @returns A boolean indicating if append should be disabled.
   */
  get appendDisabled(): boolean {
    if (this.searchResults.length !== 1) {
      return true;
    }

    const searchResult = this.searchResults[0];
    return !searchResult.permissions.modifyDocuments;
  }

  /**
   * Array of archive fields. This does not include fields belonging to a table field.
   *
   * @returns An array of fields.
   */
  get archiveFields(): Fields {
    const tableFields = this.archivesQuery.getTableFields();
    return this.archivesQuery
      .getFields()
      .filter(
        (field) => !tableFields.some((tf) => tf.fieldIds.includes(field.id))
      );
  }

  /**
   * Determines if the copy button should be disabled.
   *
   * @returns A boolean indicating if copy should be disabled.
   */
  get copyDisabled(): boolean {
    return this.appQuery.isReadOnlyLicense;
  }

  /**
   * Determines if the delete button should be disabled.
   *
   * @returns A boolean indicating if delete should be disabled.
   */
  get deleteDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.deleteDocuments
    );
  }

  /**
   * Determines if the GSE email button should be disabled.
   *
   * @returns True if GSE email should be disabled.
   */
  get emailDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.emailDocuments
    );
  }

  /**
   * Determines if the export button should be disabled.
   *
   * @returns A boolean indicating if export should be disabled.
   */
  get exportDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.exportDocuments
    );
  }

  /**
   * Determines if the download as CSV button should be disabled.
   *
   * @returns A boolean indicating if download as CSV should be disabled.
   */
  get downloadAsCsvDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.exportData
    );
  }

  /**
   * Determines if the modify button should be disabled.
   *
   * @returns True if the modify button should be disabled.
   */
  get modifyDocumentDisabled(): boolean {
    if (this.searchResults.length !== 1) {
      return true;
    }

    const searchResult = this.searchResults[0];
    const fileExtension = searchResult.fileType.toLowerCase();
    return (
      !searchResult.permissions.modifyDocuments ||
      fileExtension === '.tif' ||
      fileExtension === '.tiff'
    );
  }

  /**
   * Determines if the replace document button should be disabled.
   *
   * @returns True if the replace document button should be disabled.
   */
  get replaceDocumentDisabled(): boolean {
    if (this.searchResults.length !== 1) {
      return true;
    }

    const searchResult = this.searchResults[0];
    return !searchResult.permissions.modifyDocuments;
  }

  /**
   * Determines if the 'no annotations' option should be shown.
   *
   * @returns True if the 'no annotations' option should be shown.
   */
  get showLaunchEmailNoAnnotations(): boolean {
    return this.searchResults.every(
      (searchResult) => searchResult.permissions.modifyAnnotations
    );
  }

  /**
   * Determines if the download as CSV button should be shown.
   *
   * @returns True if the download as CSV button should be shown.
   */
  get showDownloadAsCsv(): boolean {
    // Check if the control is attached by testing if it is observed in a template (results grid is present).
    return this.downloadSelectedRowsAsCsv.observed;
  }

  /**
   * Determines if the update button should be displayed.
   *
   * @returns True if the update button should be displayed.
   */
  get showUpdate(): boolean {
    return (
      !this.hideMultiple &&
      this.supportedFeatures.versionSupports(
        this.supportedFeatures.updateMultipleSearchResults
      )
    );
  }

  /**
   * Determines if the launch button should be disabled.
   *
   * @returns True if launch should be disabled.
   */
  get launchDisabled(): boolean {
    return (
      !!this.searchesQuery.active.advancedGrouping ||
      !this.searchResults.every(
        (searchResult) =>
          searchResult.permissions.launchDocument &&
          searchResult.permissions.exportDocuments &&
          searchResult.permissions.modifyAnnotations &&
          searchResult.permissions.modifyDocumentPages &&
          searchResult.permissions.modifyDocuments
      )
    );
  }

  /**
   * Determines if the merge button should be disabled.
   *
   * @returns A boolean indicating if merge should be disabled.
   */
  get mergeDisabled(): boolean {
    return !!(
      this.searchResults.length < 2 ||
      !this.archivesQuery.active.permissions.addNewDocuments
    );
  }

  /**
   * Determines if the move button should be disabled.
   *
   * @returns A boolean indicating if move should be disabled.
   */
  get moveDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.moveDocuments
    );
  }

  /**
   * Determines if the print button should be disabled.
   *
   * @returns A boolean indicating if print should be disabled.
   */
  get printDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.printDocuments
    );
  }

  /**
   * Determines if the update button should be disabled.
   *
   * @returns True if the update button should be disabled.
   */
  get updateDisabled(): boolean {
    return !this.searchResults.every(
      (searchResult) => searchResult.permissions.modifyData
    );
  }

  /**
   * Click event for launching advanced link.
   *
   * @param advancedLink Advanced Link.
   */
  onClickAdvancedLink(advancedLink: AdvancedLink): void {
    this.logger.debug('Advanced Link clicked.', advancedLink);
    for (const row of this.searchResults) {
      let url = advancedLink.urlPattern;
      url = url.replace(new RegExp('{{Document ID}}', 'gi'), row.id.toString());
      url = url.replace(
        new RegExp('{{Archive ID}}', 'gi'),
        this.archivesQuery.activeId.toString()
      );
      url = url.replace(
        new RegExp('{{Database ID}}', 'gi'),
        this.databasesQuery.activeId.toString()
      );
      this.fieldsQuery.fields$.pipe(first()).subscribe((fields) => {
        const replacements = fields
          .map((field) => {
            const matchedField = row.fields.find((f) => f.id === field.id);
            const fieldData =
              matchedField?.value ?? matchedField?.multiValue.join(',') ?? '';
            return {
              name: field.name,
              value: fieldData,
            };
          })
          .filter((field) => field.value);

        url = applyAdvancedLinkUrlReplacements(url, replacements);
        window.open(url, '_blank');
      });
    }
  }

  /** Click event for the append button. */
  onClickAppend(): void {
    this.logger.debug('Append button clicked.');
    const dialog = this.dialog.open(InsertFromInboxDialogComponent, {
      maxWidth: this.useCompact ? '95vw' : '80vw',
    });
    dialog
      .afterClosed()
      .subscribe((result: InsertFromInboxDialogResult) =>
        this.onInsertDialogClosed(result)
      );
  }

  /** Click event for the copy button. */
  onClickCopy(): void {
    this.logger.debug('Copy clicked.');
    this.transferDocument(false);
  }

  /** Click event for the delete button. */
  onClickDelete(): void {
    this.logger.debug('Delete button was clicked.');
    const data: ConfirmationDialogData = {
      cancelActionText: 'CANCEL',
      confirmActionText: 'DELETE',
      contents: 'CONFIRM_DELETE',
      title: 'DELETE_DOCUMENTS',
    };

    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      data,
    });
    dialog.afterClosed().subscribe((confirmed) => {
      this.logger.debug('Confirm dialog closed.', confirmed);
      if (!confirmed) return;

      this.deleteSelectedDocuments();
    });
  }

  /**
   * Click event for the download CSV button.
   *
   * @param advancedConfig Determines the user should be shown advanced configuration options.
   */
  onClickDownloadCsv(advancedConfig: boolean): void {
    this.logger.debug('Download CSV clicked.', advancedConfig);
    this.downloadSelectedRowsAsCsv.emit(advancedConfig);
  }

  /**
   * Click event for the email button.
   *
   * @param includeAnnotations Whether to include annotations.
   * @param fieldId Field ID for filename.
   */
  onClickEmail(includeAnnotations: boolean, fieldId: number): void {
    this.logger.debug('Email button clicked.', includeAnnotations, fieldId);
    const searchPrompts = this.searchesQuery.currentSearchPrompts;
    const request: EmailArchiveRequest = {
      documents: this.searchResults.map(
        (row): ArchiveEmailDocument => ({
          archiveId: this.archivesQuery.activeId,
          docId: row.id,
        })
      ),
      searchCriteria: {},
    };
    for (const prompts of searchPrompts) {
      request.searchCriteria[prompts.id] = prompts.value;
    }
    this.gse.launchApi
      .emailArchiveDocument(
        this.databasesQuery.activeId,
        this.searchesQuery.activeId,
        fieldId,
        includeAnnotations,
        request
      )
      .subscribe({
        error: (error: UserFriendlyError) => this.notifications.error(error),
      });
  }

  /** Click event for the enhanced export button. */
  onClickEnhancedExport(all: boolean): void {
    this.logger.debug('Enhanced export button clicked.');
    const exportDialogData: EnhancedExportDialogData = {
      databaseId: this.databasesQuery.activeId,
      exportDocuments: all
        ? undefined
        : {
            [this.archivesQuery.activeId]: this.searchResults.map(
              (row) => row.id
            ),
          },
      exportAllSearchResults: all,
    };
    // Confirm that the user wants to do a search export if all is passed into this function.
    // Otherwise just treat the flow as if confirmation was given.
    const exportAllConfirmation$ = all
      ? ConfirmationDialogComponent.open(this.dialog, {
          data: {
            cancelActionText: 'NO',
            confirmActionText: 'YES',
            contents: 'CONFIRM_EXPORT_ALL',
            title: 'EXPORT_DOCUMENTS',
          },
        })
          .afterClosed()
          .pipe(map((result) => !!result)) // Ensure the confirmation result is always a bool.
      : of(true);
    exportAllConfirmation$
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            this.logger.warn('User cancelled search export.');
            return of();
          }
          return this.dialog
            .open(EnhancedExportDialogComponent, {
              data: exportDialogData,
            })
            .afterClosed();
        })
      )
      .subscribe({
        next: () => {
          this.logger.debug('Enhanced export dialog closed.');
        },
      });
  }

  /** Click event for the export button. */
  onClickExport(): void {
    this.logger.debug('Export button clicked.');

    for (const searchResult of this.searchResults) {
      const downloadFileUrl = this.documentProvider.getArchiveDownloadUrl(
        this.databasesQuery.activeId,
        this.archivesQuery.activeId,
        searchResult.id,
        this.auth.user.token,
        searchResult.secureId
      );

      this.windowService
        .fetchAndDownloadUrl(
          downloadFileUrl,
          `ExportedFile${searchResult.fileType}`
        )
        .subscribe();
    }
  }

  /** Click event for the insert from search button. */
  onClickInsertFromSearch(): void {
    this.logger.debug('Insert from search clicked.');
    const dialog = this.dialog.open(InsertFromSearchDialogComponent, {
      data: this.searchResults[0],
      maxWidth: this.useCompact ? '95vw' : '80vw',
    });
    dialog.afterClosed().subscribe((result: InsertFromSearchDialogResult) => {
      this.logger.debug('Insert from search dialog closed.', result);
      this.onInsertSearchDialogClosed(result);
    });
  }

  /**
   * Click event for the launch document button.
   */
  onClickLaunchDocument(): void {
    this.logger.debug('Launch document button clicked.', this.searchResults);
    this.searchResults.map((row) => {
      this.gse.launchApi
        .launchDocument(
          this.databasesQuery.activeId,
          this.archivesQuery.activeId,
          row.id,
          this.searchesQuery.activeId,
          0
        )
        .subscribe({
          next: (launchId) => {
            // We need to poll the gse api for the status every second until it is 'closed'.
            const launchStatusInterval = interval(1000)
              .pipe(
                untilDestroyed(this), // Ensure this interval is unsubscribed when this component is destroyed.
                switchMap(() =>
                  this.gse.launchApi.getStatus(launchId).pipe(
                    tap((status) => {
                      if (status === 'closed') {
                        launchStatusInterval.unsubscribe();
                        this.refreshSearch.emit({
                          forceReload: true,
                        });
                      }
                    })
                  )
                )
              )
              .subscribe();
          },
          error: (error: UserFriendlyError) => this.notifications.error(error),
        });
    });
  }

  /** Click event for the merge button. */
  onClickMerge(): void {
    this.logger.debug('Merge button clicked.', this.searchResults);
    const mergeDocuments = this.searchResults.map(
      (row) => new ArchiveMergeDocument(this.archivesQuery.activeId, row.id)
    );
    const target: MergeTarget = {
      appendType: AppendType.toEnd,
      id: this.archivesQuery.activeId,
      mergeTargetType: MergeTargetType.archive,
    };
    const progressDialog =
      this.progressDialogService.openProgressDialog('MERGING_DOCUMENTS');
    this.archiveProvider
      .mergeDocuments(
        this.databasesQuery.activeId,
        target,
        mergeDocuments[0],
        mergeDocuments.slice(1)
      )
      .pipe(
        switchMap((mergeResult) => {
          if (this.searchResults.some((r) => !r.permissions.deleteDocuments)) {
            // Do not show the merge result dialog because the user cannot delete all the source documents anyway.
            return of(mergeResult);
          }
          const dialog = MergeDocumentsResultDialogComponent.openDialog(
            mergeResult.documentId,
            this.dialog
          );
          return dialog.afterClosed().pipe(
            switchMap((dialogResult) => {
              if (!dialogResult || !dialogResult.deleteSourceDocuments) {
                return of(mergeResult);
              }

              const deleteDocuments$ = this.searchResults.map((result) =>
                this.archiveProvider
                  .deleteDocument(
                    this.databasesQuery.activeId,
                    this.archivesQuery.activeId,
                    result.id,
                    result.secureId
                  )
                  .pipe(
                    catchError((error) => {
                      // Getting here means the user had delete permissions when the merge happened but somehow
                      // still failed to delete the documents so notify and suppress the error in the rest of the pipe.
                      const userFriendlyError: UserFriendlyError = {
                        description: '',
                        error: error,
                        i18n: 'DELETE_FAILED',
                      };
                      this.notifications.error(userFriendlyError);
                      return of(undefined); // Ignore the error and emit a value so the rest of the pipe can continue.
                    })
                  )
              );
              return combineLatest(deleteDocuments$).pipe(
                map(() => mergeResult)
              );
            })
          );
        }),
        finalize(() => progressDialog.close())
      )
      .subscribe({
        next: (mergeResult) => {
          this.logger.debug('Merge created a new document.', mergeResult);
          this.notifications.success('MERGE_SUCCESSFUL');
          this.refreshSearch.emit();
        },
        error: (error: UserFriendlyError) => {
          this.notifications.error(error);
        },
      });
  }

  /** Click event for the modify button. */
  onClickModify(): void {
    this.modifyDocumentEnabled = !this.modifyDocumentEnabled;
    this.modifyDocumentEnabledChange.emit(this.modifyDocumentEnabled);
  }

  /** Click event for the move button. */
  onClickMove(): void {
    this.logger.debug('Move button clicked.');
    this.transferDocument(true);
  }

  /**
   * Click event for the open documents menu button.
   *
   * @param forceExternalViewer If the documents should be opened only with the external viewer.
   */
  onClickOpenDocuments(forceExternalViewer = false): void {
    this.logger.debug('Open documents button clicked.');
    this.openSelectedDocuments.emit({
      forceExternalViewer,
      searchResults: this.searchResults,
    });
  }

  /**
   * Click event for the print documents button.
   *
   * @param annotations Include annotations.
   */
  onClickPrintDocuments(annotations: boolean): void {
    this.logger.debug('Print documents button clicked.');
    const data: ConfirmationDialogData = {
      cancelActionText: 'CANCEL',
      confirmActionText: 'PRINT',
      contents: this.translate.translate('CONFIRM_PRINT', {
        documentNumber: this.searchResults.length,
      }),
      title: 'PRINT_DOCUMENTS',
    };

    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      data,
    });
    dialog.afterClosed().subscribe((confirmed) => {
      this.logger.debug('Confirm dialog closed.', confirmed);
      if (!confirmed) return;

      this.printDocuments(annotations);
    });
  }

  /** Handler for the replace document button click event. */
  onClickReplaceDocument(): void {
    this.logger.debug('Replace document button clicked.');
    if (this.searchResults.length !== 1) {
      this.logger.warn('Only one document can be replaced at a time.');
      return;
    }
    const searchResult = this.searchResults[0];
    const fileInputElement = this.fileInput.nativeElement;
    const clickHandler = () => {
      // Ensures the file input is cleared so that the change event always fires
      // even if the same file is selected in the picker.
      fileInputElement.value = '';
    };
    const fileChangeHandler = async () => {
      const file = fileInputElement.files
        ? fileInputElement.files[0]
        : undefined;
      assertExists(
        file,
        'A file must be provided in order to replace a document.'
      );
      const progressDialog =
        this.progressDialogService.openProgressDialog('REPLACING_DOCUMENT');
      const fileArrayBuffer = await file.arrayBuffer();
      const fileBytes = new Uint8Array(fileArrayBuffer);
      this.logger.debug('Replacing document with: ', file);
      const updateSession = new DocumentUpdateSession(true, true, false, false);
      // Prevent duplicate event listeners from being attached.
      fileInputElement.removeEventListener('change', fileChangeHandler);
      fileInputElement.removeEventListener('click', clickHandler);

      // Create update document observable.
      const updateDocument$ = this.documentUpdateProvider.updateDocument(
        this.databasesQuery.activeId,
        this.archivesQuery.activeId,
        searchResult.id,
        searchResult.secureId,
        fileBytes,
        file.name,
        file.type,
        updateSession
      );

      // Create update annotations observable.
      const updateAnnotations$ =
        this.documentUpdateProvider.updateAnnotationData(
          this.databasesQuery.activeId,
          this.archivesQuery.activeId,
          searchResult.id,
          searchResult.secureId,
          new Map<number, DocumentAnnotations>(),
          updateSession
        );

      // Run the annotation update and then the document update.
      updateAnnotations$
        .pipe(
          switchMap(() => updateDocument$),
          finalize(() => {
            progressDialog.close();
          })
        )
        .subscribe(() => {
          // Ensure the grid (or document view) is reloaded to show the new document.
          this.refreshSearch.emit({
            forceReload: true,
          });

          // Check if the current pdf preview URL in the app state is this document
          // by generating the preview url and comparing it to the current value in the app state.
          const previewUrl = this.documentProvider.getArchivePreviewUrl(
            this.databasesQuery.activeId,
            this.archivesQuery.activeId,
            searchResult.id,
            this.auth.user.token,
            searchResult.secureId,
            false
          );

          if (this.appQuery.pdfPreviewUrl === previewUrl) {
            this.logger.debug(
              'PDF preview is showing document that was replaced. It will be cleared from the app state.'
            );
            this.appService.clearPdfPreviewUrl();
          }
        });
    };

    const data: ConfirmationDialogData = {
      cancelActionText: 'CANCEL',
      confirmActionText: 'REPLACE_DOCUMENT',
      contents: 'CONFIRM_REPLACE_DOCUMENT',
      title: 'REPLACE_DOCUMENT',
    };

    ConfirmationDialogComponent.open(this.dialog, { data })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          this.logger.warn('User cancelled document replacement.');
          return;
        }

        fileInputElement.addEventListener('change', fileChangeHandler);
        fileInputElement.addEventListener('click', clickHandler);
        fileInputElement.click();
      });
  }

  /** Handler for the send to Quickbooks click event. */
  onClickSendToQuickbooks(): void {
    this.logger.debug('Send to Quickbooks clicked.');
    this.notifications.info('PUSHING_DATA_TO_QUICKBOOKS');
    const pushObservables: Observable<void>[] = [];
    let erroredDocumentCount = 0;
    for (const searchResult of this.searchResults) {
      const quickbooksPush$ = this.gse.quickbooksApi
        .startPush(
          this.databasesQuery.activeId,
          this.archivesQuery.activeId,
          searchResult.id
        )
        .pipe(
          catchError((error: UserFriendlyError) => {
            this.logger.error(
              `Failed to push document with id ${searchResult.id} to Quickbooks`,
              error
            );
            erroredDocumentCount++;
            return EMPTY;
          })
        );
      pushObservables.push(quickbooksPush$);
    }

    concat(...pushObservables)
      .pipe(
        finalize(() => {
          const jobFinishedTranslation = 'QUICKBOOKS_PUSH_JOB_COMPLETED';
          if (erroredDocumentCount > 0) {
            this.notifications.info(jobFinishedTranslation);
            const error: UserFriendlyError = {
              description: '',
              error: undefined,
              i18n: 'QUICKBOOKS_PUSH_ERROR',
              i18nParameters: {
                erroredDocumentCount: `${erroredDocumentCount}`,
              },
            };
            this.notifications.error(error);
          } else {
            this.notifications.success(jobFinishedTranslation);
            this.refreshSearch.emit({
              reloadOnlyDocViewIndexData: true, // this only has an effect if this action was taken from the document view.
              forceReload: true, // This only has an effect if this action was taken from the archive view.
            });
          }
        })
      )
      .subscribe();
  }

  /** Handler for the update button click event. */
  onClickUpdateSelected(): void {
    this.logger.debug('Update selected documents clicked.');
    const dialog = UpdateMultipleSearchResultsDialogComponent.open(
      this.dialog,
      {
        databaseId: this.databasesQuery.activeId,
        archiveId: this.archivesQuery.activeId,
        searchResults: this.searchResults,
      }
    );
    dialog.afterClosed().subscribe((result) => {
      if (!result) {
        this.logger.debug('Update selected documents dialog cancelled.');
        return;
      }

      // Getting here means the dialog was not cancelled and completed the update action so request a search refresh.
      this.refreshSearch.emit();
    });
  }

  /**
   * Opens the menu.
   *
   * @param mouseEvent Mouse event.
   * @param searchResults Row nodes.
   * @param copyValue Optional value that can be copied to the clipboard if the menu supports the copy value action.
   */
  openMenu(
    mouseEvent: MouseEvent,
    searchResults: SearchResults,
    copyValue?: string
  ): void {
    // Set the search results to act on
    if (searchResults) this.searchResults = searchResults;
    this.logger.debug(
      'Opening archive actions menu.',
      mouseEvent,
      searchResults
    );
    if (copyValue) {
      this.copyValue.set(copyValue);
      this.logger.debug('Copy value set for menu.', copyValue);
    }
    this.open(mouseEvent);
  }

  private deleteSelectedDocuments(): void {
    const deletes$: Observable<void>[] = [];
    let erroredDeletesCount = 0;
    for (const row of this.searchResults) {
      const delete$ = this.archiveProvider
        .deleteDocument(
          this.databasesQuery.activeId,
          this.archivesQuery.activeId,
          row.id,
          row.secureId
        )
        .pipe(
          catchError((error: UserFriendlyError) => {
            this.logger.error(
              `Failed to delete document with id ${row.id}`,
              error
            );
            erroredDeletesCount++;
            return EMPTY;
          })
        );
      deletes$.push(delete$);
    }

    combineLatest(deletes$)
      .pipe(finalize(() => this.onDeleteDocumentsFinalize(erroredDeletesCount)))
      .subscribe();
  }

  private onDeleteDocumentsFinalize(erroredDeletesCount: number): void {
    const translatedMessage = this.translate.translate(
      'DOCUMENT_DELETE_FINISHED'
    );
    if (erroredDeletesCount === 0) {
      this.notifications.success(translatedMessage);
    } else {
      this.notifications.info(translatedMessage);
      const error: UserFriendlyError = {
        description: '',
        error: undefined,
        i18n: 'DOCUMENTS_FAILED_TO_DELETE_ERROR',
        i18nParameters: {
          errorCount: `${erroredDeletesCount}`,
        },
      };
      this.notifications.error(error);
    }
    this.refreshSearch.emit({
      ids: this.searchResults.map((searchResult) => searchResult.id),
    });
  }

  private onInsertDialogClosed(result: InsertFromInboxDialogResult): void {
    if (!result) {
      this.logger.debug('Insert from inbox dialog cancelled.');
      return;
    }

    this.logger.debug('Insert from inbox will occur.', result);
    const target: MergeTarget = {
      appendType: result.appendType,
      id: this.archivesQuery.activeId,
      mergeTargetType: MergeTargetType.archive,
    };
    const baseDocument = new ArchiveMergeDocument(
      this.archivesQuery.activeId,
      this.searchResults[0].id
    );
    const mergeDocuments = result.files.map(
      (file) => new InboxMergeDocument(result.inboxId, file)
    );
    const progressDialog = this.progressDialogService.openProgressDialog(
      'APPENDING_DOCUMENTS'
    );
    this.archiveProvider
      .mergeDocuments(
        this.databasesQuery.activeId,
        target,
        baseDocument,
        mergeDocuments
      )
      .pipe(finalize(() => progressDialog.close()))
      .subscribe({
        next: (mergeResult) =>
          this.onInsertFromInboxSuccess(mergeResult, result),
        error: (error: UserFriendlyError) => {
          this.notifications.error(error);
        },
      });
  }

  private onInsertFromInboxSuccess(
    mergeResult: MergeResult,
    dialogResult: InsertFromInboxDialogResult
  ): void {
    this.logger.debug('Insert from inbox completed.', mergeResult);
    if (dialogResult.deleteFiles) {
      const deleteJobs$ = dialogResult.files.map((file) =>
        this.inboxProvider.deleteDocument(dialogResult.inboxId, file).pipe(
          catchError((error: UserFriendlyError) => {
            this.logger.error(error);
            this.notifications.error({
              ...error,
              i18n: 'ERROR_FAILED_TO_DELETE_FILE_FROM_INBOX',
              i18nParameters: { filename: file },
            });
            return EMPTY;
          })
        )
      );
      combineLatest(deleteJobs$)
        .pipe(
          finalize(() => {
            this.notifications.success('APPEND_SUCCESSFUL');
            this.refreshSearch.emit();
          })
        )
        .subscribe();
    } else {
      this.notifications.success('APPEND_SUCCESSFUL');
      this.refreshSearch.emit({ forceReload: true });
    }
  }

  private onInsertSearchDialogClosed(
    result: InsertFromSearchDialogResult
  ): void {
    if (!result) {
      this.logger.debug('Insert from search dialog cancelled.');
      return;
    }
    const target: MergeTarget = {
      appendType: result.appendType,
      id: this.archivesQuery.activeId,
      mergeTargetType: MergeTargetType.archive,
    };
    const baseDocument = new ArchiveMergeDocument(
      this.archivesQuery.activeId,
      this.searchResults[0].id
    );
    const mergeDocuments: ArchiveMergeDocument[] = [];
    /** Array of delete observables returning the document id of the deleted document. */
    const deleteDocuments$: Observable<number>[] = [];
    for (const [documentIdString, secureId] of Object.entries(
      result.documentIdSecureIdMap
    )) {
      const documentId = Number(documentIdString);
      mergeDocuments.push(
        new ArchiveMergeDocument(result.archiveId, documentId)
      );
      if (result.deleteFiles) {
        deleteDocuments$.push(
          this.archiveProvider
            .deleteDocument(
              this.databasesQuery.activeId,
              result.archiveId,
              documentId,
              secureId
            )
            .pipe(
              catchError((error: UserFriendlyError) => {
                this.notifications.error({
                  ...error,
                  i18n: 'ERROR_FAILED_TO_DELETE_FILE_FROM_SEARCH',
                  i18nParameters: { documentId: documentIdString },
                });
                return EMPTY;
              }),
              map(() => documentId)
            )
        );
      }
    }

    this.archiveProvider
      .mergeDocuments(
        this.databasesQuery.activeId,
        target,
        baseDocument,
        mergeDocuments
      )
      .subscribe({
        next: (mergeResult) => {
          this.logger.debug('Insert from search merge complete.', mergeResult);
          if (!result.keepBaseFile) {
            // Add a delete for the base document.
            deleteDocuments$.push(
              this.archiveProvider
                .deleteDocument(
                  this.databasesQuery.activeId,
                  baseDocument.archiveId,
                  baseDocument.docId,
                  this.searchResults[0].secureId
                )
                .pipe(map(() => baseDocument.docId))
            );
          }
          //TODO refresh emit to handle "reload" performing a redirect to the new document when we are in document view.
          if (deleteDocuments$.length > 0) {
            combineLatest(deleteDocuments$)
              .pipe(
                finalize(() => {
                  this.notifications.success('APPEND_SUCCESSFUL');
                })
              )
              .subscribe({
                next: (deletedDocumentIds) => {
                  this.refreshSearch.emit({
                    ids: deletedDocumentIds,
                    idsToAdd: [mergeResult.documentId],
                    requestedViewDocument: mergeResult.documentId,
                  });
                },
              });
          } else {
            this.notifications.success('APPEND_SUCCESSFUL');
            this.refreshSearch.emit({
              idsToAdd: [mergeResult.documentId],
              requestedViewDocument: mergeResult.documentId,
            });
          }
        },
        error: (error: UserFriendlyError) => {
          this.notifications.error({
            ...error,
            i18n: 'ERROR_UNABLE_TO_INSERT_FROM_SEARCH',
          });
        },
      });
  }

  /**
   * Merges selected documents and sends to browser as one document for printing.
   *
   * @param annotations Include annotations.
   */
  private printDocuments(annotations: boolean) {
    const progressDialog = this.progressDialogService.openProgressDialog(
      'PREPARING_DOCUMENTS_FOR_PRINT'
    );
    let fileURL = '';
    const archiveDocuments: { [key: number]: number[] } = {};
    archiveDocuments[this.archivesQuery.activeId] = this.searchResults.map(
      (row) => row.id
    );
    const batchPrint: BatchPrint = {
      annotations,
      archiveDocuments,
    };
    this.printProvider
      .printDocuments(this.databasesQuery.activeId, batchPrint)
      .subscribe({
        next: (result: any) => {
          fileURL = URL.createObjectURL(
            new Blob([result as BlobPart], { type: 'application/pdf' })
          );
          this.logger.debug('Bulk print successful:', fileURL);
          const printWindow = window.open(fileURL);
          // Attempt to force the new tab/window to print the document.
          printWindow?.print();
        },
        error: (error: UserFriendlyError) => {
          this.notifications.error(error);
        },
        complete: () => {
          URL.revokeObjectURL(fileURL);
          progressDialog.close();
        },
      });
  }

  /**
   * Determines if the specified Advanced Link be displayed in this archive context.
   *
   * @param advancedLink AdvancedLink.
   * @returns Boolean.
   */
  private shouldShowAdvancedLink(advancedLink: AdvancedLink): boolean {
    return (
      (advancedLink.archiveId === this.archivesQuery.activeId ||
        advancedLink.archiveId === 0) &&
      advancedLink.fieldId === 0
    );
  }

  /**
   * Transfer a document to a new location.
   *
   * @param move Determines if the document should be moved. Otherwise it will be copied.
   */
  private transferDocument(move: boolean): void {
    this.documentTransferService
      .startDocumentTransferFromArchive(
        this.databasesQuery.activeId,
        this.archivesQuery.activeId,
        this.searchResults,
        move
      )
      .subscribe({
        complete: () => {
          this.notifications.info('Document transfer job finished.');
          if (move) {
            this.refreshSearch.emit({
              ids: this.searchResults.map((row) => row.id),
            });
          } else {
            this.refreshSearch.emit();
          }
        },
        error: (error: UserFriendlyError) => {
          if (error.i18n !== 'OPERATION_CANCELLED_MSG')
            this.notifications.error(error);
        },
      });
  }
}
